import React, { FC, useState, useMemo } from 'react';
import { AnyFCO, DataSourceFCO, DataSourceFCOFields, FCOFields, FCOType } from '../../../core/types/fcoTypes';

import {
  ComboFilterCategory,
  ComboFilterDateSelector,
  ComboFilterHierarchicalList,
  ComboFilterHierarchyState,
  ComboFilterListSelector,
  ComboFilterRangeSelector,
  EmptyProps,
  ExtendedTableColumn,
  Link,
  Select,
  Table,
  TableViewLayout,
  Text,
  useTableFilters,
} from '@lithos';

import {
  isInBounds,
  isInDateRange,
  getFilteredItems,
  tableStringSortParameters,
  tableArrayLengthSortParameters,
  tableDateSortParameters,
} from '@lithos/TableUtils';

import { isEqual, startCase, uniq } from 'lodash';

import RelatedFCOsCell from '../fcoListView/RelatedFCOsCell';
import TagsCell from '../fcoListView/TagsCell';
import DateCell from '../fcoListView/DateCell';
import { areAnyCategoryMembersCurrentlyActive, countHierarchyActiveSelections } from '@lithos/ComboFilterUtils';
import { defaultDateState, defaultRangeState, defaultTagsState } from '../fcoListView/utils';
import TableSelectors from '../fcoListView/TableSelectors';
import TableColumnControls from '../fcoListView/TableColumnControls';
import { useUserSettings } from '../../context/UserSettingsContext';
import { FeatureFlags } from '../../context/UserSettingsContext';
import { IconType } from '@lithos/Icon';

interface DataSourcesListProps {
  items: DataSourceFCO[];
  idToFcoMap: Record<string, AnyFCO>;
  workspace: string;
}

enum DataSourcesListColumnKeys {
  NAME,
  TAGS,
  TYPE,
  BATCH_SOURCE,
  STREAM_SOURCE,
  NUMBER_OF_FEATURE_VIEWS,
  FEATURE_VIEWS,
  NUMBER_OF_FEATURE_SERVICES,
  FEATURE_SERVICES,
  CREATED_BY,
  CREATED_AT,
  LAST_UPDATED_BY,
  LAST_UPDATED_AT,
  SERVER_GROUP,
}

interface EntitiesListFilterState {
  tags: ComboFilterHierarchyState;
  type: string[];
  'Batch Source': string[];
  'Stream Source': string[];
  '# of Feature Views': [number, number];
  'Feature Views': string[];
  '# of Feature Services': [number, number];
  'Feature Services': string[];
  createdBy: string[];
  createdAt: [Date, Date];
  lastUpdatedBy: string[];
  lastUpdatedAt: [Date, Date];
  serverGroup: string[];
}

const DataSourcesList: FC<DataSourcesListProps> = ({ items, idToFcoMap, workspace }) => {
  const { featureEnabled } = useUserSettings();
  const canShowServerGroups = featureEnabled(FeatureFlags.SHOW_SERVER_GROUPS);

  // Always need to set a default filter state
  const defaultFilterState: EntitiesListFilterState = useMemo(
    (): EntitiesListFilterState => ({
      tags: defaultTagsState(items),
      type: [],
      'Batch Source': [],
      'Stream Source': [],
      '# of Feature Views': defaultRangeState(items, DataSourceFCOFields.DEPENDENT_FEATURE_VIEWS),
      'Feature Views': [],
      '# of Feature Services': defaultRangeState(items, DataSourceFCOFields.DEPENDENT_FEATURE_SERVICES),
      'Feature Services': [],
      createdBy: [],
      createdAt: defaultDateState(items, FCOFields.CREATED_DATE),
      lastUpdatedBy: [],
      lastUpdatedAt: defaultDateState(items, FCOFields.LAST_MODIFIED_DATE),
      serverGroup: [],
    }),
    [items]
  );

  const allValidOptions = useMemo(() => {
    return {
      Tags: Array.from(Object.keys(defaultTagsState(items))),
      'Batch Source': uniq(items.map((item) => item[DataSourceFCOFields.BATCH_SOURCE])).filter(
        (source) => source !== undefined
      ) as string[],
      'Stream Source': uniq(items.map((item) => item[DataSourceFCOFields.STREAM_SOURCE])).filter(
        (source) => source !== undefined
      ) as string[],
      'Feature Views': uniq(items.map((item) => item[DataSourceFCOFields.DEPENDENT_FEATURE_VIEWS]))
        .flat()
        .filter((source) => source !== undefined) as string[],
      'Feature Services': uniq(items.map((item) => item[DataSourceFCOFields.DEPENDENT_FEATURE_SERVICES]))
        .flat()
        .filter((source) => source !== undefined) as string[],
      Owner: uniq(items.map((item) => item[FCOFields.OWNER] ?? '')).filter(
        (source) => source !== undefined
      ) as string[],
      'Last Updated By': uniq(items.map((item) => item[FCOFields.LAST_MODIFIED_BY] ?? '')).filter(
        (source) => source !== undefined
      ) as string[],
      'Ingest Server Group': uniq(items.map((item) => item[DataSourceFCOFields.INGEST_SERVER_GROUP_NAME] ?? '')).filter(
        Boolean
      ),
    };
  }, [items]);

  // Shared logic and states for filtered table views
  const {
    searchQuery,
    hasAnyFilters,
    activeFilterCount,
    sortType,
    setSortType,
    activeHeading,
    setActiveHeading,
    filterState,
    onSearchInputChange,
    handleResetFilters,
    handleFilterChange,
  } = useTableFilters<DataSourceFCO, DataSourcesListColumnKeys, EntitiesListFilterState>(defaultFilterState);

  // Filter components are the actual input components (e.g. A combo filter listt)
  // These are defined separately from the ComboFilters because they are reused in the table header
  // They may also be reused in shortcut popovers in the general filter list
  // This allows us to reference the same component in multiple places
  const filterComponents: Record<
    DataSourcesListColumnKeys,
    {
      filter: React.ReactNode;
      activeCount: () => number;
      filterCallback: (source: DataSourceFCO) => boolean;
      name: string;
    }
  > = useMemo(() => {
    return {
      [DataSourcesListColumnKeys.NAME]: {
        filter: <></>,
        activeCount: () => 0,
        filterCallback: () => true,
        name: 'Name',
      },
      [DataSourcesListColumnKeys.TAGS]: {
        filter: (
          <ComboFilterHierarchicalList
            items={filterState.tags}
            setItems={(tags) => {
              handleFilterChange(filterComponents, 'tags', tags);
            }}
          />
        ),
        activeCount: () => countHierarchyActiveSelections(filterState.tags),
        filterCallback: (source) => {
          let hasEachCategoryMatched = true;
          const categories = Object.keys(filterState.tags);
          categories.forEach((category) => {
            if (areAnyCategoryMembersCurrentlyActive(filterState.tags[category])) {
              const activeSubcategories = Object.entries(filterState.tags[category])
                .filter((entry) => entry[1])
                .map((result) => result[0]);
              const hasMatch = source.tags.find(
                (tag) => tag.key === category && activeSubcategories.includes(tag.value)
              );
              if (hasMatch === undefined) {
                hasEachCategoryMatched = false;
              }
            }
          });

          return hasEachCategoryMatched;
        },
        name: 'Tags',
      },
      [DataSourcesListColumnKeys.TYPE]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.type}
            setSelected={(type) => {
              handleFilterChange(filterComponents, 'type', type);
            }}
            items={['Batch', 'Stream']}
            title={''}
          />
        ),
        activeCount: () => filterState.type.length,
        filterCallback: (source) => {
          return filterState.type.includes(source[DataSourceFCOFields.SOURCE_TYPE]);
        },
        name: 'Type',
      },
      [DataSourcesListColumnKeys.BATCH_SOURCE]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['Batch Source']}
            setSelected={(type) => handleFilterChange(filterComponents, 'Batch Source', type)}
            items={uniq(
              items
                .map((item) => item[DataSourceFCOFields.BATCH_SOURCE])
                .filter((source) => source !== undefined) as string[]
            )
              .sort()
              .map(startCase)}
            title={''}
          />
        ),
        activeCount: () => filterState['Batch Source'].length,
        filterCallback: (source) => {
          return filterState['Batch Source'].includes(startCase(source[DataSourceFCOFields.BATCH_SOURCE]) ?? '');
        },
        name: 'Batch Source',
      },
      [DataSourcesListColumnKeys.STREAM_SOURCE]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['Stream Source']}
            setSelected={(type) => handleFilterChange(filterComponents, 'Stream Source', type)}
            items={uniq(
              items
                .map((item) => item[DataSourceFCOFields.STREAM_SOURCE])
                .filter((source) => source !== undefined) as string[]
            )
              .sort()
              .map(startCase)}
            title={''}
          />
        ),
        activeCount: () => filterState['Stream Source'].length,
        filterCallback: (source) => {
          return filterState['Stream Source'].includes(startCase(source[DataSourceFCOFields.STREAM_SOURCE]) ?? '');
        },
        name: 'Stream Source',
      },
      [DataSourcesListColumnKeys.NUMBER_OF_FEATURE_VIEWS]: {
        filter: (
          <ComboFilterRangeSelector
            value={filterState['# of Feature Views']}
            setValue={(range) => {
              handleFilterChange(filterComponents, '# of Feature Views', range);
            }}
            bounds={defaultFilterState['# of Feature Views']}
          />
        ),
        activeCount: () =>
          isEqual(filterState['# of Feature Views'], defaultFilterState['# of Feature Views']) ? 0 : 1,
        filterCallback: (source) => {
          return isInBounds(
            source[DataSourceFCOFields.DEPENDENT_FEATURE_VIEWS].length,
            filterState['# of Feature Views']
          );
        },
        name: '# of Feature Views',
      },
      [DataSourcesListColumnKeys.FEATURE_VIEWS]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['Feature Views']}
            setSelected={(type) => handleFilterChange(filterComponents, 'Feature Views', type)}
            icon={'FeatureView'}
            items={uniq(
              items
                .map((item) => item[DataSourceFCOFields.DEPENDENT_FEATURE_VIEWS])
                .flat()
                .filter((source) => source !== undefined)
                .map((id) => idToFcoMap[id][FCOFields.NAME])
            )
              .sort()
              .map(startCase)}
            title={''}
          />
        ),
        activeCount: () => filterState['Feature Views'].length,
        filterCallback: (source) => {
          const featureViewNames = source[DataSourceFCOFields.DEPENDENT_FEATURE_VIEWS].map(
            (featureViewId) => idToFcoMap[featureViewId]
          );
          return (
            filterState['Feature Views'].length === 0 ||
            featureViewNames.some((fv) => filterState['Feature Views'].includes(startCase(fv[FCOFields.NAME])))
          );
        },
        name: 'Feature Views',
      },
      [DataSourcesListColumnKeys.NUMBER_OF_FEATURE_SERVICES]: {
        filter: (
          <ComboFilterRangeSelector
            value={filterState['# of Feature Services']}
            setValue={(range) => {
              handleFilterChange(filterComponents, '# of Feature Services', range);
            }}
            bounds={defaultFilterState['# of Feature Services']}
          />
        ),
        activeCount: () =>
          isEqual(filterState['# of Feature Services'], defaultFilterState['# of Feature Services']) ? 0 : 1,
        filterCallback: (source) => {
          return isInBounds(
            source[DataSourceFCOFields.DEPENDENT_FEATURE_SERVICES].length,
            filterState['# of Feature Services']
          );
        },
        name: '# of Feature Services',
      },
      [DataSourcesListColumnKeys.FEATURE_SERVICES]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['Feature Services']}
            setSelected={(type) => handleFilterChange(filterComponents, 'Feature Services', type)}
            icon="FeatureService"
            items={uniq(
              items
                .map((item) => item[DataSourceFCOFields.DEPENDENT_FEATURE_SERVICES])
                .flat()
                .filter((source) => source !== undefined)
                .map((id) => idToFcoMap[id][FCOFields.NAME])
            )
              .sort()
              .map(startCase)}
            title={''}
          />
        ),
        activeCount: () => filterState['Feature Services'].length,
        filterCallback: (source) => {
          const featureServiceNames = source[DataSourceFCOFields.DEPENDENT_FEATURE_SERVICES]
            .map((featureServiceId) => idToFcoMap[featureServiceId])
            .map((featureService) => featureService.name);

          return (
            filterState['Feature Services'].length === 0 ||
            featureServiceNames.some((featureService) =>
              filterState['Feature Services'].includes(startCase(featureService))
            )
          );
        },
        name: 'Feature Services',
      },
      [DataSourcesListColumnKeys.LAST_UPDATED_BY]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.lastUpdatedBy}
            setSelected={(type) => handleFilterChange(filterComponents, 'lastUpdatedBy', type)}
            items={uniq(
              items.map((item) => item[FCOFields.LAST_MODIFIED_BY] ?? '').filter((source) => source !== undefined)
            ).sort()}
            title={''}
          />
        ),
        activeCount: () => filterState.lastUpdatedBy.length,
        filterCallback: (source) => {
          return filterState.lastUpdatedBy.includes(source[FCOFields.LAST_MODIFIED_BY] ?? '');
        },
        name: 'Last Updated By',
      },
      [DataSourcesListColumnKeys.LAST_UPDATED_AT]: {
        filter: (
          <ComboFilterDateSelector
            start={filterState.lastUpdatedAt[0]}
            setStart={(date) => {
              handleFilterChange(filterComponents, 'lastUpdatedAt', [date as Date, filterState.lastUpdatedAt[1]]);
            }}
            end={filterState.lastUpdatedAt[1]}
            setEnd={(date) => {
              handleFilterChange(filterComponents, 'lastUpdatedAt', [filterState.lastUpdatedAt[0], date as Date]);
            }}
          />
        ),
        activeCount: () => (isEqual(filterState.lastUpdatedAt, defaultFilterState.lastUpdatedAt) ? 0 : 1),
        filterCallback: (source) => {
          return isInDateRange(source[FCOFields.LAST_MODIFIED_DATE], filterState.lastUpdatedAt);
        },
        name: 'Last Updated At',
      },
      [DataSourcesListColumnKeys.CREATED_BY]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.createdBy}
            setSelected={(type) => handleFilterChange(filterComponents, 'createdBy', type)}
            items={uniq(
              items.map((item) => item[FCOFields.OWNER] ?? '').filter((source) => source !== undefined)
            ).sort()}
            title={''}
          />
        ),
        activeCount: () => filterState.createdBy.length,
        filterCallback: (source) => {
          return filterState.createdBy.includes(source[FCOFields.OWNER] ?? '');
        },
        name: 'Owner',
      },
      [DataSourcesListColumnKeys.CREATED_AT]: {
        filter: (
          <ComboFilterDateSelector
            start={filterState.createdAt[0]}
            setStart={(date) => {
              handleFilterChange(filterComponents, 'createdAt', [date as Date, filterState.createdAt[1]]);
            }}
            end={filterState.lastUpdatedAt[1]}
            setEnd={(date) => {
              handleFilterChange(filterComponents, 'createdAt', [filterState.createdAt[0], date as Date]);
            }}
          />
        ),
        activeCount: () => (isEqual(filterState.createdAt, defaultFilterState.createdAt) ? 0 : 1),
        filterCallback: (source) => {
          return isInDateRange(source[FCOFields.CREATED_DATE], filterState.createdAt);
        },
        name: 'Created At',
      },
      [DataSourcesListColumnKeys.SERVER_GROUP]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.serverGroup}
            setSelected={(serverGroup) => handleFilterChange(filterComponents, 'serverGroup', serverGroup)}
            items={allValidOptions['Ingest Server Group']}
            title={''}
          />
        ),
        activeCount: () => filterState.serverGroup.length,
        filterCallback: (source) => {
          return filterState.serverGroup.includes(source[DataSourceFCOFields.INGEST_SERVER_GROUP_NAME] ?? '');
        },
        name: 'Ingest Server Group',
      },
    };
  }, [filterState, items, defaultFilterState, allValidOptions, handleFilterChange, idToFcoMap]);

  // These define the filter categories in the Filter button popover
  const categories: ComboFilterCategory[] = [
    {
      name: 'Tags',
      icon: 'Tag',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'tags', defaultFilterState.tags);
      },
      activeCount: countHierarchyActiveSelections(filterState.tags),
      popover: filterComponents[DataSourcesListColumnKeys.TAGS].filter,
      disabled: allValidOptions['Tags'].length === 0,
    },
    {
      name: 'Type',
      icon: 'Sources',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'type', defaultFilterState.type);
      },
      activeCount: filterState.type.length,
      popover: filterComponents[DataSourcesListColumnKeys.TYPE].filter,
    },
    {
      name: 'Batch Source',
      icon: 'Sources',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Batch Source', defaultFilterState['Batch Source']);
      },
      activeCount: filterState['Batch Source'].length,
      popover: filterComponents[DataSourcesListColumnKeys.BATCH_SOURCE].filter,
      disabled: allValidOptions['Batch Source'].length === 0,
    },
    {
      name: 'Stream Source',
      icon: 'Sources',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Stream Source', defaultFilterState['Stream Source']);
      },
      activeCount: filterState['Stream Source'].length,
      popover: filterComponents[DataSourcesListColumnKeys.STREAM_SOURCE].filter,
      disabled: allValidOptions['Stream Source'].length === 0,
    },
    {
      name: '# of Feature Views',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, '# of Feature Views', defaultFilterState['# of Feature Views']);
      },
      activeCount: isEqual(filterState['# of Feature Views'], defaultFilterState['# of Feature Views']) ? 0 : 1,
      popover: filterComponents[DataSourcesListColumnKeys.NUMBER_OF_FEATURE_VIEWS].filter,
    },
    {
      name: 'Feature Views',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Feature Views', defaultFilterState['Feature Views']);
      },
      activeCount: filterState['Feature Views'].length,
      popover: filterComponents[DataSourcesListColumnKeys.FEATURE_VIEWS].filter,
      disabled: allValidOptions['Feature Views'].length === 0,
    },
    {
      name: '# of Feature Services',
      icon: 'FeatureService',
      resetCallback: () => {
        handleFilterChange(filterComponents, '# of Feature Services', defaultFilterState['# of Feature Services']);
      },
      activeCount: isEqual(filterState['# of Feature Services'], defaultFilterState['# of Feature Services']) ? 0 : 1,
      popover: filterComponents[DataSourcesListColumnKeys.NUMBER_OF_FEATURE_SERVICES].filter,
    },
    {
      name: 'Feature Services',
      icon: 'FeatureService',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Feature Services', defaultFilterState['Feature Services']);
      },
      activeCount: filterState['Feature Services'].length,
      popover: filterComponents[DataSourcesListColumnKeys.FEATURE_SERVICES].filter,
      disabled: allValidOptions['Feature Services'].length === 0,
    },
    {
      name: 'Owner',
      icon: 'UserCircle',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'createdBy', defaultFilterState.createdBy);
      },
      activeCount: filterState.createdBy.length,
      popover: filterComponents[DataSourcesListColumnKeys.CREATED_BY].filter,
      disabled: allValidOptions['Owner'].length === 0,
    },
    {
      name: 'Created At',
      icon: 'Calendar',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'createdAt', defaultFilterState.createdAt);
      },
      activeCount: isEqual(filterState.createdAt, defaultFilterState.createdAt) ? 0 : 1,
      popover: filterComponents[DataSourcesListColumnKeys.CREATED_AT].filter,
    },
    {
      name: 'Last Updated By',
      icon: 'UserCircle',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'lastUpdatedBy', defaultFilterState.lastUpdatedBy);
      },
      activeCount: filterState.lastUpdatedBy.length,
      popover: filterComponents[DataSourcesListColumnKeys.LAST_UPDATED_BY].filter,
      disabled: allValidOptions['Last Updated By'].length === 0,
    },
    {
      name: 'Last Updated At',
      icon: 'Calendar',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'lastUpdatedAt', defaultFilterState.lastUpdatedAt);
      },
      activeCount: isEqual(filterState.lastUpdatedAt, defaultFilterState.lastUpdatedAt) ? 0 : 1,
      popover: filterComponents[DataSourcesListColumnKeys.LAST_UPDATED_AT].filter,
    },
    ...(canShowServerGroups
      ? [
          {
            name: 'Ingest Server Group',
            icon: 'IngestServerGroup' as IconType,
            resetCallback: () => {
              handleFilterChange(filterComponents, 'serverGroup', defaultFilterState.serverGroup);
            },
            activeCount: filterState.serverGroup.length,
            popover: filterComponents[DataSourcesListColumnKeys.SERVER_GROUP].filter,
          },
        ]
      : []),
  ];

  // Define the columns for the table
  const columns: ExtendedTableColumn<DataSourceFCO>[] = [
    {
      name: 'Name',
      id: DataSourcesListColumnKeys.NAME,
      content: (source) => (
        <Link
          name={source[FCOFields.NAME]}
          icon="Sources"
          url={`/repo/${workspace}/data-sources/${source[FCOFields.NAME]}/overview`}
          testId={`data-sources-link-${source[FCOFields.NAME]}`}
        />
      ),
      columnWidth: 'l',
      ...tableStringSortParameters(sortType, FCOFields.NAME, setSortType),
    },
    {
      name: 'Tags',
      id: DataSourcesListColumnKeys.TAGS,
      content: (source) => <TagsCell fco={source} />,
      filterContent: filterComponents[DataSourcesListColumnKeys.TAGS].filter,
      ...tableArrayLengthSortParameters(sortType, FCOFields.TAGS, setSortType),
    },
    {
      name: 'Type',
      id: DataSourcesListColumnKeys.TYPE,
      content: (source) => <Text size="s">{source[DataSourceFCOFields.SOURCE_TYPE]}</Text>,
      filterContent: filterComponents[DataSourcesListColumnKeys.TYPE].filter,
      ...tableStringSortParameters(sortType, DataSourceFCOFields.SOURCE_TYPE, setSortType),
    },
    {
      name: 'Batch Source',
      id: DataSourcesListColumnKeys.BATCH_SOURCE,
      content: (source) => <Text size="s">{startCase(source[DataSourceFCOFields.BATCH_SOURCE])}</Text>,
      filterContent: filterComponents[DataSourcesListColumnKeys.BATCH_SOURCE].filter,
      ...tableStringSortParameters(sortType, DataSourceFCOFields.BATCH_SOURCE, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Stream Source',
      id: DataSourcesListColumnKeys.STREAM_SOURCE,
      content: (source) =>
        source[DataSourceFCOFields.SOURCE_TYPE] === 'Batch' ? (
          <Text size="s">n/a</Text>
        ) : (
          <Text size="s">{startCase(source[DataSourceFCOFields.STREAM_SOURCE])}</Text>
        ),
      filterContent: filterComponents[DataSourcesListColumnKeys.STREAM_SOURCE].filter,
      ...tableStringSortParameters(sortType, DataSourceFCOFields.STREAM_SOURCE, setSortType),
    },
    {
      name: 'Feature Views',
      id: DataSourcesListColumnKeys.FEATURE_VIEWS,
      content: (source) => {
        const relatedFeatureViews = source[DataSourceFCOFields.DEPENDENT_FEATURE_VIEWS].map((id) => idToFcoMap[id]);
        return <RelatedFCOsCell workspace={workspace} fcos={relatedFeatureViews} type={FCOType.FEATURE_VIEW} />;
      },
      filterContent: filterComponents[DataSourcesListColumnKeys.FEATURE_VIEWS].filter,
      ...tableArrayLengthSortParameters(sortType, DataSourceFCOFields.DEPENDENT_FEATURE_VIEWS, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Feature Services',
      id: DataSourcesListColumnKeys.FEATURE_SERVICES,
      content: (source) => {
        const relatedFeatureServices = source[DataSourceFCOFields.DEPENDENT_FEATURE_SERVICES].map(
          (id) => idToFcoMap[id]
        );
        return <RelatedFCOsCell workspace={workspace} fcos={relatedFeatureServices} type={FCOType.FEATURE_SERVICE} />;
      },
      filterContent: filterComponents[DataSourcesListColumnKeys.FEATURE_SERVICES].filter,
      ...tableArrayLengthSortParameters(sortType, DataSourceFCOFields.DEPENDENT_FEATURE_SERVICES, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Last Updated By',
      id: DataSourcesListColumnKeys.LAST_UPDATED_BY,
      content: (source) => (
        <Link avatar={{ name: source[FCOFields.LAST_MODIFIED_BY] }} name={source[FCOFields.LAST_MODIFIED_BY]} noHover />
      ),
      filterContent: filterComponents[DataSourcesListColumnKeys.LAST_UPDATED_BY].filter,
      columnWidth: 'm',
      ...tableStringSortParameters(sortType, FCOFields.LAST_MODIFIED_BY, setSortType),
    },
    {
      name: 'Last Updated At',
      id: DataSourcesListColumnKeys.LAST_UPDATED_AT,
      content: (source) => <DateCell date={source[FCOFields.LAST_MODIFIED_DATE]} />,
      filterContent: filterComponents[DataSourcesListColumnKeys.LAST_UPDATED_AT].filter,
      ...tableDateSortParameters(sortType, FCOFields.LAST_MODIFIED_DATE, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Owner',
      id: DataSourcesListColumnKeys.CREATED_BY,
      content: (source) => <Link noHover avatar={{ name: source[FCOFields.OWNER] }} name={source[FCOFields.OWNER]} />,
      filterContent: filterComponents[DataSourcesListColumnKeys.CREATED_BY].filter,
      ...tableStringSortParameters(sortType, FCOFields.OWNER, setSortType),
    },
    {
      name: 'Created At',
      id: DataSourcesListColumnKeys.CREATED_AT,
      content: (source) => <DateCell date={source[FCOFields.CREATED_DATE]} />,
      filterContent: filterComponents[DataSourcesListColumnKeys.CREATED_AT].filter,
      ...tableDateSortParameters(sortType, FCOFields.CREATED_DATE, setSortType),
      columnWidth: 'm',
    },
    ...(canShowServerGroups
      ? [
          {
            name: 'Ingest Server Group',
            id: DataSourcesListColumnKeys.SERVER_GROUP,
            content: (source: DataSourceFCO) =>
              source[DataSourceFCOFields.INGEST_SERVER_GROUP_NAME] === undefined ? (
                <>-</>
              ) : (
                <Link
                  icon="TransformServerGroup"
                  name={source[DataSourceFCOFields.INGEST_SERVER_GROUP_NAME]}
                  url={`/repo/${workspace}/server-groups/ingest/${source[DataSourceFCOFields.INGEST_SERVER_GROUP_ID]}`}
                />
              ),
            columnWidth: 'm' as const,
          },
        ]
      : []),
  ];

  const selectableColumns = columns.map((column) => {
    return { name: column.name as string, visible: true };
  });

  const [columnState, setColumnState] = useState<{ name: string; visible: boolean }[]>(selectableColumns);

  const columnsSelector = (
    <Select
      groups={[
        {
          items: columnState.map((column) => {
            return {
              name: column.name,
              asToggle: true,
              onClick: () => {
                const newColumnState = columnState.map((selectableColumn) => {
                  if (selectableColumn.name !== column.name) {
                    return selectableColumn;
                  }

                  return {
                    name: selectableColumn.name,
                    visible: !selectableColumn.visible,
                  };
                });
                setColumnState && setColumnState(newColumnState);
              },
              selected: column.visible,
              selectable: true,
            };
          }),
        },
      ]}
      filterable={false}
    />
  );

  const filterEmpty: EmptyProps = {
    actions: <></>,
    placement: 'full-page',
    useCase: 'empty',
    title: 'No Results',
    description: 'No Sources match the current filters. Try adjusting your filters or search query.',
  };

  const filteredColumns = columns
    .map((column) => {
      const matching = columnState.find((stateColumn) => stateColumn.name === column.name);
      if (!matching) {
        return;
      }
      if (matching?.visible !== true) {
        return;
      }

      const asColumn: ExtendedTableColumn<DataSourceFCO> = {
        ...column,
        isActive: column.id === activeHeading,
        setIsActive: (arg) => {
          if (arg) {
            setActiveHeading(column.id as DataSourcesListColumnKeys);
            return;
          }
          setActiveHeading(undefined);
        },
        hideCallback: () => {
          const newColumnState = columnState.map((selectableColumn) => {
            if (selectableColumn.name !== column.name) {
              return selectableColumn;
            }

            return {
              name: selectableColumn.name,
              visible: !selectableColumn.visible,
            };
          });

          setColumnState && setColumnState(newColumnState);
        },
        columnsContent: columnsSelector,
      };

      return asColumn;
    })
    .filter((column) => column !== undefined) as ExtendedTableColumn<DataSourceFCO>[];

  const activeFilters = Object.values(filterComponents).filter((filter) => {
    return filter.activeCount() !== 0;
  });

  const filteredResults = getFilteredItems(
    items,
    activeFilters,
    [FCOFields.NAME, FCOFields.ID],
    searchQuery ?? '',
    sortType
  );

  return (
    <>
      <TableViewLayout
        searchInput={
          <TableSelectors
            hasAnyFilters={hasAnyFilters}
            activeFilterCount={activeFilterCount}
            handleResetFilters={handleResetFilters}
            categories={categories}
            onSearchInputChange={onSearchInputChange}
          />
        }
        filterAndColumnControls={<TableColumnControls columnsSelector={columnsSelector} />}
        table={
          <>
            <Table
              items={filteredResults}
              columns={filteredColumns}
              frozenFirstColumn={true}
              emptyProps={filterEmpty}
              memoized={true}
            />
          </>
        }
      />
    </>
  );
};

export default DataSourcesList;
